/**
 * One class's slice of a category, on a page that shows every class.
 *
 * The class pages render a section for the class you are inside. Here the
 * scope is inverted — one category, every class you teach — so each card
 * carries the class name and reuses the very same section component beneath
 * it. Only classes that need the full class query pay for it.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '~/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible';
import type { SectionKey } from '~/components/Sidebar/contextNav';
import { useClassDetailQuery } from '../Courses/useClassDetailQuery';
import type { ClassOfferingDetail } from '../Courses/classDetailTypes';
import type { TeachingClass } from './teachingContext';
import ClassHealthRoster from '../Courses/ClassHealthRoster';
import AssignmentsSection from '../Courses/AssignmentsSection';
import GradebookGrid from '../Courses/GradebookGrid';
import AttendanceReport from '../Courses/AttendanceReport';
import StandardsAssessmentSection from '../Courses/StandardsAssessmentSection';

/** Sections that need the roster, gradebooks and assignments loaded. */
const NEEDS_CLASS_DETAIL: ReadonlySet<SectionKey> = new Set(['people', 'assignments', 'grades']);

interface ClassSectionCardProps {
  classOffering: TeachingClass;
  section: SectionKey;
  classPath: string;
}

function ClassSectionCard({ classOffering, section, classPath }: ClassSectionCardProps) {
  // Open by default: the point of these pages is to see where everything
  // stands. Collapsing is for getting a class you have already dealt with out
  // of the way, so it is a per-visit convenience rather than saved state.
  const [open, setOpen] = useState(true);
  const needsDetail = NEEDS_CLASS_DETAIL.has(section);
  const { data, loading, refetch } = useClassDetailQuery(needsDetail ? classOffering.id : undefined);
  const offering = data?.classOffering as ClassOfferingDetail | undefined;

  const rosterEntries = offering?.rosterEntries ?? [];
  const activeEntries = rosterEntries.filter((entry) => entry.state === 'ACTIVE');
  const droppedEntries = rosterEntries.filter((entry) => entry.state !== 'ACTIVE');

  const body = () => {
    if (needsDetail && loading && !offering) {
      return <p className="text-sm text-muted-foreground">Loading…</p>;
    }
    if (needsDetail && !offering) {
      return <p className="text-sm text-muted-foreground">This class could not be loaded.</p>;
    }

    switch (section) {
      case 'people':
        return activeEntries.length === 0 && droppedEntries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No students on this roster yet.</p>
        ) : (
          <ClassHealthRoster
            classOfferingId={classOffering.id}
            activeEntries={activeEntries}
            droppedEntries={droppedEntries}
            isAdmin={false}
            onDropStudent={() => {}}
          />
        );
      case 'assignments':
        return (
          <AssignmentsSection
            classOfferingId={classOffering.id}
            gradebooks={offering!.gradebooks ?? []}
            assignments={offering!.assignments ?? []}
            onChanged={() => refetch()}
          />
        );
      case 'grades':
        return (
          <GradebookGrid
            classOfferingId={classOffering.id}
            gradebooks={offering!.gradebooks ?? []}
            assignments={offering!.assignments ?? []}
            onChanged={() => refetch()}
          />
        );
      case 'attendance':
        // A teacher reaches this through My Teaching, so they never manage it.
        return (
          <AttendanceReport
            classOfferingId={classOffering.id}
            scheduleBlocks={classOffering.scheduleBlocks}
            canManage={false}
          />
        );
      case 'standards':
        return <StandardsAssessmentSection classOfferingId={classOffering.id} canConfigure={false} />;
      default:
        return null;
    }
  };

  return (
    <Card>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CardHeader className={open ? undefined : 'pb-6'}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex items-start gap-2 text-left"
                aria-label={`${open ? 'Collapse' : 'Expand'} ${classOffering.displayName}`}
              >
                {open
                  ? <ChevronDown className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                  : <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />}
                <span>
                  <CardTitle className="text-base">{classOffering.displayName}</CardTitle>
                  <CardDescription>
                    {classOffering.course?.name} — Section {classOffering.section}
                  </CardDescription>
                </span>
              </button>
            </CollapsibleTrigger>
            <Link
              to={`${classPath}/${section}`}
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              Open class
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent>{body()}</CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

export default ClassSectionCard;
